import React, { useState } from 'react';
import styled from '@emotion/styled';
import {
  Content,
  Text,
  Spacing,
  devices,
  Stack,
  WhiteButton,
  BlueButtonBasic,
} from './common';
import Modal from './Modal';
import { ExternalLink } from './SVG';
import Markdown from './Markdown';
import { CampaignEmployeeListingSection, Employee } from '../@types/types';
import { RichTextContent } from './StyledComponents';
import ProfilePhotoPlaceholder from '../../static/assets/profile_photo_placeholder.png';

const DEFAULT_SECTION_FONT = '#000000';

const hasBg = (c?: string) => c != null && c !== '';

const sectionFont = (c?: string) => c ?? DEFAULT_SECTION_FONT;

export type EmployeeListingSectionProps = {
  section: CampaignEmployeeListingSection;
  employees: Employee[];
};

/** Campaign employee block: same 3-column grid, round photos, and detail modal as `english-services`. */
const EmployeeListingSection = ({
  section,
  employees: campaignEmployees,
}: EmployeeListingSectionProps) => {
  const [showModal, setShowModal] = useState(false);
  const [modalEmployee, setModalEmployee] = useState<Employee | null>(null);

  const openModal = (employee: Employee) => {
    setModalEmployee(employee);
    setShowModal(true);
  };

  const closeModal = (open: boolean) => {
    setShowModal(open);
    if (!open) {
      setModalEmployee(null);
    }
  };

  const bg = section.BackgroundColor;
  const fg = sectionFont(section.FontColor);
  const lightestBlue = !hasBg(bg);

  return (
    <>
      {modalEmployee && (
        <Modal
          showModal={showModal}
          setShowModal={closeModal}
          employeeData={modalEmployee}
        />
      )}
      <SectionContainer
        backgroundColor={bg}
        fontColor={fg}
        lightestBlue={lightestBlue}
      >
        <InnerContent>
          {section.Title ? (
            <>
              <Text variant="title-2" color="inherit">
                {section.Title}
              </Text>
              <Spacing axis="y" amount="medium" />
            </>
          ) : null}
          {section.Text ? (
            <>
              <RichTextContent>{section.Text}</RichTextContent>
              <Spacing axis="y" amount="xxlarge" />
            </>
          ) : null}

          {campaignEmployees.length !== 0 ? (
            <EmployeeContainer>
              {campaignEmployees.map((employee: Employee, index: number) => (
                <ProfileContainer key={employee.Name + index}>
                  <ContentContainer axis="y" spacing="medium" align="center">
                    <RoundImage
                      src={
                        employee.Picture?.publicURL ?? ProfilePhotoPlaceholder
                      }
                      alt=""
                    />
                    <ProfileDetails>
                      <NameAndTitle>
                        <NameText variant="subtitle">{employee.Name}</NameText>
                        <TitleText variant="body">{employee.Title}</TitleText>
                      </NameAndTitle>

                      {employee.Description && (
                        <DescriptionSection>
                          {employee.Description}
                        </DescriptionSection>
                      )}

                      <ExternalLinks>
                        {employee.ReservationLink && (
                          <Button href={employee.ReservationLink}>
                            Varaa aika
                            <CustomIcon fill="#fff" />
                          </Button>
                        )}

                        <ModalButton
                          onClick={() => openModal(employee)}
                          linkExist={employee.ReservationLink ?? ''}
                        >
                          Lisätietoa
                        </ModalButton>
                      </ExternalLinks>
                    </ProfileDetails>
                  </ContentContainer>
                </ProfileContainer>
              ))}
            </EmployeeContainer>
          ) : null}
        </InnerContent>
      </SectionContainer>
    </>
  );
};

const SectionContainer = styled.div<{
  lightestBlue?: boolean;
  backgroundColor?: string;
  fontColor?: string;
}>`
  background-color: ${(props) =>
    hasBg(props.backgroundColor)
      ? props.backgroundColor
      : props.lightestBlue
      ? props.theme.colors.lightestBlue
      : props.theme.colors.lighterBlue};
  color: ${(props) => props.fontColor ?? DEFAULT_SECTION_FONT};
  padding: 40px 0 100px;

  @media ${devices.tablet} {
    padding: ${(props) => props.theme.spacing.default} 0 80px;
  }
`;

const InnerContent = styled(Content)`
  padding: 0 80px;

  @media ${devices.tablet} {
    padding: 0 ${(props) => props.theme.spacing.default};
  }

  @media ${devices.mobileXL} {
    width: 95%;
  }
`;

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 30%;
  align-items: center;
  padding-bottom: 80px;

  @media ${devices.tablet} {
    flex: 0 0 50%;

    &:last-child {
      padding-bottom: 0px;
    }
  }
`;

const ContentContainer = styled(Stack)`
  width: 300px;

  @media ${devices.tablet} {
    width: 100%;
  }
`;

const RoundImage = styled.img`
  border-radius: 50%;
  height: 300px;
  width: 300px;
  margin: auto;
  object-fit: cover;

  @media ${devices.mobileS} {
    height: 200px;
    width: 200px;
  }
`;

const ProfileDetails = styled(Stack)`
  display: flex;
  flex-direction: column;
  margin-top: ${(props) => props.theme.spacing.medium};
  text-align: center;

  @media ${devices.mobileXL} {
    margin: auto;
  }
`;

const NameText = styled(Text)`
  font-size: ${(props) => props.theme.sizing.medium};

  @media ${devices.tablet} {
    text-align: center;
  }
`;

const TitleText = styled(Text)`
  font-style: italic;
  font-size: calc((${(props) => props.theme.sizing.default}) + 1px);

  @media ${devices.tablet} {
    text-align: center;
  }
`;

const DescriptionSection = styled(Markdown)`
  line-height: 20px;

  @media ${devices.tablet} {
    text-align: center;
  }
`;

const ExternalLinks = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  @media ${devices.tablet} {
    flex-direction: column;
  }
`;

const CustomIcon = styled(ExternalLink)`
  margin-left: 10px;
`;

const NameAndTitle = styled.div`
  @media ${devices.mobileXL} {
    margin-top: ${(props) => props.theme.sizing.default};
  }
`;

const Button = styled(BlueButtonBasic)`
  @media ${devices.tablet} {
    width: 80%;
    max-width: 300px;
    margin: ${(props) => props.theme.sizing.default} 0px;
    justify-content: space-between;
  }
`;

const ModalButton = styled(WhiteButton)<{ linkExist: string }>`
  cursor: pointer;
  margin: ${(props) => !props.linkExist && 'auto'};
  width: max-content;
`;

const EmployeeContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 20px;

  @media ${devices.laptopXS} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${devices.mobileXL} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

export default EmployeeListingSection;
